import { option } from 'fp-ts';
import { PricingModeKey } from '@mews/enums';
import { LoggerService } from '@mews/framework';
import { getCurrencyCode } from '../../modules/app/selectors';
import { getChainName } from '../../modules/entities/chains/selectors';
import { getSelectedCityName } from '../../modules/entities/cities/selectors';
import {
    getEnterpriseChainId,
    getEnterpriseDefaultCurrencyCode,
    getEnterpriseDefaultLanguageCode,
    getEnterpriseName,
    hasEnterpriseNetPricing,
} from '../../modules/entities/enterprises/selectors';
import { convertPrice } from '../../modules/entities/exchangeRates/viewSelectors';
import { getCurrentProductPrice } from '../../modules/entities/productPricing/viewSelectors';
import { getProductEnterpriseId } from '../../modules/entities/products/selectors';
import { localizeProperty } from '../../modules/globalization/selectors';
import { getSelectedEnterpriseCategoryLowestPrice } from '../../modules/pricing/views/categoriesPricing';
import { ExtendedCategory } from '../../modules/resourceCategories/types';
import { getVoucherCode } from '../../modules/resourceOrder/selectors';
import { ApplicationState } from '../../types';
import { Item } from '../ga4EventService';

interface CommonItemDataProps {
    enterpriseId: string;
    itemCategory?: string;
}

const getCommonItemData = (state: ApplicationState, { enterpriseId, itemCategory }: CommonItemDataProps) => {
    const defaultLanguageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
    const enterpriseNames = getEnterpriseName(state, { enterpriseId });
    const voucherCode = getVoucherCode(state);

    const enterpriseName = localizeProperty(state, {
        property: enterpriseNames,
        languageCode: defaultLanguageCode,
    });
    const cityNames = getSelectedCityName(state);
    const cityName = localizeProperty(state, {
        property: cityNames,
        languageCode: defaultLanguageCode,
    });
    const chainId = getEnterpriseChainId(state, { enterpriseId });
    const chainName = getChainName(state, { chainId });

    return {
        item_category: itemCategory,
        item_category2: enterpriseName ?? undefined,
        item_category3: cityName ?? undefined,
        item_brand: chainName ?? undefined,
        affiliation: enterpriseName,
        enterpriseId,
        coupon: voucherCode,
    };
};

interface GetCategoryItemsProps {
    state: ApplicationState;
    category: ExtendedCategory;
    loggerService: LoggerService;
}

export const getCategoryItem = ({ state, category, loggerService }: GetCategoryItemsProps): Item | null => {
    const { enterpriseId, id, name } = category;
    const defaultLanguageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
    const isNetPricing = hasEnterpriseNetPricing(state, { enterpriseId });
    const pricingModeKey = isNetPricing ? PricingModeKey.NetValue : PricingModeKey.GrossValue;
    const prices = getSelectedEnterpriseCategoryLowestPrice(state, { categoryId: category.id });
    const currency = getCurrencyCode(state) as string;
    const price = prices?.[currency]?.[pricingModeKey];

    if (price == null) {
        loggerService.log('ga4-category-error', 'Category price not found', {
            productId: id,
            name,
            useEnterpriseCurrency: true,
        });

        return null;
    }

    const enterpriseCurrency = getEnterpriseDefaultCurrencyCode(state, { enterpriseId });
    const enterprisePrice = convertPrice(state, {
        value: price,
        sourceCurrencyCode: currency,
        targetCurrencyCode: enterpriseCurrency,
        currencyCode: enterpriseCurrency,
        enterpriseId,
    });

    const commonData = getCommonItemData(state, { enterpriseId });
    return {
        item_id: id,
        item_name: localizeProperty(state, {
            property: name,
            languageCode: defaultLanguageCode,
        }),
        ...commonData,
        currency: enterpriseCurrency,
        price: enterprisePrice,
    };
};

interface GetProductItemsProps {
    state: ApplicationState;
    id: string;
    name: string;
    quantity: number;
    loggerService: LoggerService;
}

export interface ProductItem extends Item {
    price: number;
    quantity: number;
}

export const getProductItem = ({
    state,
    id,
    name,
    quantity,
    loggerService,
}: GetProductItemsProps): ProductItem | null => {
    const enterpriseId = getProductEnterpriseId(state, { id });
    const productPrice = option.toNullable(getCurrentProductPrice(state, { productId: id }));

    if (enterpriseId == null) {
        loggerService.log('ga4-product-error', 'Product data not found', {
            productId: id,
            name,
            useEnterpriseCurrency: true,
            productPrice,
        });
        return null;
    }
    const isNetPricing = hasEnterpriseNetPricing(state, { enterpriseId });
    const currency = getCurrencyCode(state) as string;
    const pricingModeKey = isNetPricing ? PricingModeKey.NetValue : PricingModeKey.GrossValue;
    const enterpriseCurrency = getEnterpriseDefaultCurrencyCode(state, { enterpriseId });

    const priceKnown = productPrice != null;
    // Relative product doesn't have a price, but for sake of listing it in `view_items_list` we'll set product price to 0;
    const price = priceKnown ? productPrice?.totalAmount?.[currency]?.[pricingModeKey] : 0;

    const enterprisePrice = convertPrice(state, {
        value: price,
        sourceCurrencyCode: currency,
        targetCurrencyCode: enterpriseCurrency,
        currencyCode: enterpriseCurrency,
        enterpriseId,
    });
    const commonData = getCommonItemData(state, { enterpriseId, itemCategory: 'Product' });

    return {
        item_id: id,
        item_name: name,
        currency: enterpriseCurrency,
        price: enterprisePrice,
        price_known: priceKnown,
        quantity, // TODO this might depend on ProductChargingMode - and we should calculate it.
        ...commonData,
    };
};
